import { pinLayer, placeLayers } from './map-place-layers';
import { LngLat, MapPlace } from './map-types';

/**
 * The selected suburb's place markers and the pin of a place being added by hand (#253 step 3): what they are, which
 * is selected, and what a click on the map means while they are showing. A click on a marker goes to its pick handler
 * (not the suburb under it); in pin mode a click anywhere moves the pin instead.
 */
export class PlaceMarkers {
  places: MapPlace[] = [];
  selected: number | null = null;
  pin: LngLat | null = null;

  private onPlacePick: ((id: number) => void) | null = null;
  private onPinPick: ((lng: number, lat: number) => void) | null = null;

  /** @param redraw draws the map again after a change here. */
  constructor(private redraw: () => void) {}

  set(places: MapPlace[], onPick: (id: number) => void): void {
    this.places = places;
    this.onPlacePick = onPick;
    if (this.selected !== null && !places.some((p) => p.id === this.selected)) this.selected = null;
    this.redraw();
  }

  select(id: number | null): void {
    this.selected = id;
    this.redraw();
  }

  /** Pin mode on (with where the pin starts, and who hears where it moves) or off (null, null). */
  setPin(pin: LngLat | null, onPick: ((lng: number, lat: number) => void) | null): void {
    this.pin = pin;
    this.onPinPick = onPick;
    this.redraw();
  }

  find(id: number): MapPlace | undefined {
    return this.places.find((x) => x.id === id);
  }

  /** In pin mode a click anywhere moves the pin; true when it did. */
  pinClick(info: any): boolean {
    if (!this.onPinPick || !Array.isArray(info?.coordinate)) return false;
    const [lng, lat] = info.coordinate;
    this.pin = { lng, lat };
    this.redraw();
    this.onPinPick(lng, lat);
    return true;
  }

  /** The markers' and the pin's deck.gl layers (map-place-layers.ts), above the suburbs. */
  layers(deck: any, zoom: number): any[] {
    return [
      ...placeLayers(deck, this.places, this.selected, zoom, (info) => this.placeClick(info)),
      pinLayer(deck, this.pin),
    ];
  }

  private placeClick(info: any): boolean {
    if (this.pinClick(info)) return true;
    const id = info?.object?.id;
    if (!id) return false;
    this.selected = id;
    this.redraw();
    this.onPlacePick?.(id);
    return true;
  }
}
